import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { DataService } from 'src/app/services/data.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { DashboardView } from 'src/app/services/query-context/models/dashboard-view.model';

export interface SavedContextMetadata {
    name: string;
    isPublic: boolean;
    overwrite?: boolean;
    id?: string;
}

export interface ISavedDashboardViewComponentOptions {
    /**
     * Whether to show the navigation `<` and `>` buttons
     * @default false
     */
    showNavigationButtons?: boolean;
}

@Component({
    selector: 'app-saved-dashboard-view',
    templateUrl: './saved-dashboard-view.component.html',
    styleUrls: ['./saved-dashboard-view.component.scss'],
})
export class SavedDashboardViewComponent implements OnInit {
    EMPTY_CONTEXT_METADATA: SavedContextMetadata = {
        name: '',
        isPublic: false,
        overwrite: undefined,
        id: undefined,
    };
    @Input() options: ISavedDashboardViewComponentOptions = {};

    @Output() saveNamedContextEvent = new EventEmitter<{
        name: string;
        isPublic: boolean;
        overwrite?: boolean;
        id?: string;
    }>();

    // contextMetadata: SavedContextMetadata = { ...this.EMPTY_CONTEXT_METADATA };

    modalContextMetadata: SavedContextMetadata = {
        ...this.EMPTY_CONTEXT_METADATA,
    };
    modalErrorMessage = '';

    savedUserContexts: DashboardView[] = [];

    isModalVisible = false;
    dashboardName = '';

    currentDashboardViewId: string | undefined = undefined;

    canSaveViewReason = '';
    canSaveView = true;

    constructor(
        private logger: LoggerService,
        private data: DataService,
        private dashboard: DashboardService
    ) {}

    ngOnInit(): void {
        this.data
            .getCurrentUserNamedContexts({
                dashboardId: this.dashboard.DASHBOARD_ID,
            })
            .then((contexts: DashboardView[]) => {
                this.savedUserContexts = contexts;
            });
        this.dashboard.cannotSaveCurrentDashboardViewReason$.subscribe(
            (reason: string) => {
                this.canSaveViewReason = reason;
                this.canSaveView = reason === '';
            }
        );
    }

    async onSaveView() {
        if (!this.dashboard.currentDashboardView) {
            throw new Error('No view selected');
        }
        await this.dashboard.updateDashboardView(
            this.dashboard.currentDashboardView
        );
    }

    onSaveViewAs() {
        this.showViewDetailsModal();
    }

    async onQuickShareView() {
        await this.dashboard.saveDashboardView();
    }

    showViewDetailsModal() {
        this.data
            .getCurrentUserNamedContexts({
                dashboardId: this.dashboard.DASHBOARD_ID,
            })
            .then((contexts: DashboardView[]) => {
                this.savedUserContexts = contexts;
            });
        this.isModalVisible = true;
        this.dashboardName = this.dashboard.DASHBOARD_NAME;
    }

    handleViewDetailsModalOk() {
        let validateResult = this.validateModal();
        if (!validateResult.valid) {
            this.logger.error(validateResult.message);
            this.modalErrorMessage = validateResult.message;
            return;
        } else {
            this.modalErrorMessage = '';
        }
        this.saveDashboardView();
        this.isModalVisible = false;
    }

    validateModal(): { valid: boolean; message: string } {
        const reason: string[] = [];
        if (
            this.modalContextMetadata.overwrite &&
            !this.modalContextMetadata.id
        ) {
            // add to reason
            reason.push('Context id is required for overwrite.');
        }
        if (!this.modalContextMetadata.name.trim()) {
            reason.push('Context name cannot be empty.');
        }
        return { valid: reason.length == 0, message: reason.join('\n') };
    }

    handleViewDetailsModalCancel() {
        this.isModalVisible = false;
    }

    saveDashboardView() {
        // if overwrite then contextId should be present
        if (
            this.modalContextMetadata.overwrite &&
            !this.modalContextMetadata.id
        ) {
            this.logger.error('Context id is required for overwrite.');
            return;
        }
        if (this.modalContextMetadata.name.trim()) {
            this.dashboard.saveAsNamedDashboardView({
                name: this.modalContextMetadata.name,
                isPublic: this.modalContextMetadata.isPublic,
                overwrite: this.modalContextMetadata.overwrite,
                // if overwrite then contextId should be present
                id: this.modalContextMetadata.overwrite
                    ? this.modalContextMetadata.id
                    : undefined,
            });
        } else {
            this.logger.error('Context name cannot be empty.');
        }
        this.modalContextMetadata = { ...this.EMPTY_CONTEXT_METADATA };
    }

    getContextDisplayName(context: DashboardView): string {
        return `${context.name} (${context.id})`;
    }

    onModalContextChange(contextId: string) {
        this.modalContextMetadata.id = contextId;
        const dashboardView = this.savedUserContexts.find(
            (context) => context.id === contextId
        );
        this.modalContextMetadata.name = dashboardView?.name || '';
        this.modalContextMetadata.isPublic = dashboardView?.isPublic || false;
    }
}
